import React, { useEffect, useState } from "react";
import { LogIn, LogOut, Camera, Activity } from "lucide-react";
import api, { fmtDate, fmtTime } from "../../lib/api";
import { Loading, EmptyState, PageHeader, StatusPill } from "../../components/Shared";

export default function Visits() {
  const [visits, setVisits] = useState(null);
  const [sel, setSel] = useState(null);
  useEffect(() => { api.get("/family/visits").then((r) => setVisits(r.data)).catch(() => setVisits([])); }, []);
  const open = async (id) => { const r = await api.get(`/family/visits/${id}`); setSel(r.data); };
  if (!visits) return <Loading />;
  return (
    <div data-testid="visits-page">
      <PageHeader title="Visit Reports" sub="After every ParentOne visit — check-in, tasks, observations and follow-ups." />
      {visits.length === 0 ? <EmptyState title="No visits yet" body="Visit reports will appear here after your Care Manager's first visit." /> : (
        <div className="grid gap-6 lg:grid-cols-5">
          <div className="space-y-3 lg:col-span-2">
            {visits.map((v) => (
              <button key={v.id} onClick={() => open(v.id)}
                className={`w-full rounded-2xl border p-4 text-left transition-colors ${sel?.id === v.id ? "border-emerald-800 bg-emerald-50" : "border-stone-200 bg-white hover:border-stone-300"}`}
                data-testid={`visit-item-${v.id}`}>
                <div className="flex items-center justify-between">
                  <p className="font-heading font-semibold text-stone-900">{v.purpose}{v.parent_name ? <span className="ml-2 text-xs font-medium text-stone-400">· {v.parent_name}</span> : null}</p>
                  <StatusPill status={v.status} />
                </div>
                <p className="mt-1 text-sm text-stone-500">{fmtDate(v.scheduled_at)}</p>
              </button>
            ))}
          </div>
          <div className="lg:col-span-3">
            {!sel ? (
              <div className="p1-card flex h-full items-center justify-center p-10 text-sm text-stone-400" data-testid="visit-detail-empty">Select a visit to view its report.</div>
            ) : (
              <div className="p1-card p-6" data-testid="visit-report-detail">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <h2 className="font-heading text-xl font-bold text-stone-900">{sel.purpose}</h2>
                  <StatusPill status={sel.status} />
                </div>
                <p className="text-sm text-stone-500">Care Manager: {sel.care_manager?.name || "—"}</p>
                <div className="mt-4 grid gap-3 sm:grid-cols-2">
                  <div className="flex items-center gap-2 rounded-xl bg-stone-50 p-3 text-sm"><LogIn className="h-4 w-4 text-emerald-800" /> Arrived: <strong>{fmtTime(sel.check_in_at)}</strong></div>
                  <div className="flex items-center gap-2 rounded-xl bg-stone-50 p-3 text-sm"><LogOut className="h-4 w-4 text-emerald-800" /> Left: <strong>{fmtTime(sel.check_out_at)}</strong></div>
                </div>
                {sel.report ? (
                  <div className="mt-5 space-y-5">
                    <div>
                      <p className="p1-label">Tasks completed</p>
                      <div className="flex flex-wrap gap-2">{sel.report.tasks_completed?.map((t) => (
                        <span key={t} className="rounded-full bg-emerald-50 px-3 py-1 text-xs font-medium text-emerald-900">{t}</span>
                      ))}</div>
                    </div>
                    <div><p className="p1-label">General observations</p><p className="text-sm leading-relaxed text-stone-700">{sel.report.observations || "—"}</p></div>
                    {sel.report.vitals && Object.keys(sel.report.vitals).length > 0 && (
                      <div>
                        <p className="p1-label flex items-center gap-1"><Activity className="h-3.5 w-3.5" /> Vitals (recorded by authorised personnel)</p>
                        <div className="flex flex-wrap gap-2">
                          {Object.entries(sel.report.vitals).map(([k, v]) => v && (
                            <span key={k} className="rounded-xl border border-stone-200 px-3 py-1.5 text-xs"><span className="uppercase text-stone-400">{k}</span> <strong className="text-stone-800">{v}</strong></span>
                          ))}
                        </div>
                      </div>
                    )}
                    {sel.report.note && <div className="rounded-xl bg-emerald-50 p-4"><p className="p1-label !text-emerald-800">Care Manager note</p><p className="text-sm text-emerald-950">{sel.report.note}</p></div>}
                    {sel.report.followups?.length > 0 && (
                      <div><p className="p1-label">Follow-up actions</p>
                        <ul className="list-inside list-disc text-sm text-stone-700">{sel.report.followups.map((f) => <li key={f}>{f}</li>)}</ul></div>
                    )}
                    <p className="flex items-center gap-1.5 text-xs text-stone-400"><Camera className="h-3.5 w-3.5" /> Photos & documents are shared only where consent exists.</p>
                  </div>
                ) : (
                  <p className="mt-5 rounded-xl bg-sky-50 p-4 text-sm text-sky-900" data-testid="visit-pending-note">This visit hasn't happened yet. The report will appear here right after check-out.</p>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
